import apiClient from './apiClient';

// Type definitions
export interface ReferralStats {
  referralCode: string;
  totalReferrals: number;
  activeSignups: number;
  subscribed: number;
  minutesEarned: number;
  moneyEarned: number;
}

export interface ReferredUser {
  id: string;
  name: string;
  email: string;
  date: string; // ISO date string
  hasSubscribed: boolean;
  plan?: string;
  minutesEarned: number;
}

export interface ReferralHistoryParams {
  from?: string; // ISO date string
  to?: string; // ISO date string
  page?: number;
  limit?: number;
}

export interface ReferralHistoryResponse {
  total: number;
  page: number;
  limit: number;
  data: ReferredUser[];
}

export interface ClaimableReward {
  id: string;
  type: 'minutes' | 'money' | 'discount' | 'feature';
  amount: number;
  description: string;
  source: string; // e.g., "signup", "subscription"
  refereeId: string;
  refereeName: string;
  date: string; // ISO date string
}

export interface ReferralReward {
  type: 'minutes' | 'money' | 'discount' | 'feature';
  amount: number;
  description: string;
}

// Ambassador-related interfaces
export interface AmbassadorStatus {
  isAmbassador: boolean;
  status: string | null;
  application: AmbassadorApplication | null;
}

export interface AmbassadorApplication {
  _id: string;
  userId: string;
  status: 'pending' | 'approved' | 'rejected' | 'suspended';
  totalEarnings: number;
  totalPayouts: number;
  pendingEarnings: number;
  totalReferrals: number;
  successfulReferrals: number;
  appliedAt: string;
  approvedAt?: string;
}

export interface AmbassadorEarnings {
  totalEarnings: number;
  pendingEarnings: number;
  totalPayouts: number;
  totalReferrals: number;
  successfulReferrals: number;
}

export interface PayoutInfo {
  id: string;
  status: string;
  amount: number;
  currency: string;
  utr: string | null;
  mode: string;
  reference_id: string;
  fees: number;
  tax: number;
  created_at: number;
  status_details?: any;
}

export interface PayoutHistoryResponse {
  payouts: PayoutInfo[];
  total: number;
}

class ReferralService {
  /**
   * Get the current user's referral code
   */
  async getMyReferralCode(): Promise<string> {
    try {
      const response = await apiClient.get<{ referralCode: string }>('/user-referral/my-code');
      return response.referralCode;
    } catch (error) {
      console.error('Error fetching referral code:', error);
      throw error;
    }
  }
  
  /**
   * Get referral statistics for the current user
   */
  async getReferralStatistics(): Promise<ReferralStats> {
    try {
      console.log('[REFERRAL] Fetching referral statistics');
      const response = await apiClient.get<any>('/user-referral/statistics');
      
      console.log('[REFERRAL] Raw statistics response:', response);
      console.log('[REFERRAL] Referral code from API:', response.referralCode);
      
      // Map the response to our frontend type
      const stats = {
        referralCode: response.referralCode || '',
        totalReferrals: response.totalReferrals || 0,
        activeSignups: response.activeSignups || 0,
        subscribed: response.subscriptionsPurchased || 0,
        minutesEarned: response.minutesEarned || 0,
        moneyEarned: response.moneyEarned || 0
      };
      
      console.log('[REFERRAL] Mapped statistics:', stats);
      
      return stats;
    } catch (error) {
      console.error('[REFERRAL] Error fetching referral statistics:', error);
      throw error;
    }
  }
  
  /**
   * Get referral history with optional date filters
   */
  async getReferralHistory(params?: ReferralHistoryParams): Promise<ReferralHistoryResponse> {
    try {
      // Build query params
      const queryParams = new URLSearchParams();
      if (params?.from) queryParams.append('from', params.from);
      if (params?.to) queryParams.append('to', params.to);
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      
      const queryString = queryParams.toString();
      const url = `/user-referral/history${queryString ? '?' + queryString : ''}`;
      
      const response = await apiClient.get<any>(url);
      
      // Map the response data to our frontend type
      const mappedData = response.data.map((item: any) => ({
        id: item.referee?._id || '',
        name: item.referee?.name || 'Unknown User',
        email: item.referee?.email || '',
        date: item.usedAt || new Date().toISOString(),
        hasSubscribed: item.actionType === 'subscription',
        plan: item.planId ? 'Subscription Plan' : undefined,
        minutesEarned: item.rewardAmount || 0
      }));
      
      return {
        total: response.total || 0,
        page: response.page || 1,
        limit: response.limit || 10,
        data: mappedData
      };
    } catch (error) {
      console.error('Error fetching referral history:', error);
      throw error;
    }
  }
  
  /**
   * Exchange/apply a referral code
   */
  async exchangeReferralCode(referralCode: string): Promise<{ success: boolean; rewards: ReferralReward[] }> {
    try {
      const response = await apiClient.post<any>('/user-referral/exchange-code', { 
        referralCode 
      });
      
      return {
        success: response.success || false,
        rewards: response.rewards || []
      };
    } catch (error) {
      console.error('Error exchanging referral code:', error);
      throw error;
    }
  }
  
  /**
   * Get referral information by code
   */
  async getReferralInfo(referralCode: string): Promise<any> {
    try {
      const response = await apiClient.get<any>(`/user-referral/code/${referralCode}`);
      return response;
    } catch (error) {
      console.error('Error fetching referral info:', error);
      throw error;
    }
  }
  
  /**
   * Get claimable rewards from referrals
   */
  async getClaimableRewards(): Promise<ClaimableReward[]> {
    try {
      const response = await apiClient.get<any>('/user-referral/claimable-rewards');
      
      // Map the response to our frontend type
      return response.rewards.map((reward: any) => ({
        id: reward.id || '',
        type: reward.type || 'minutes',
        amount: reward.amount || 0,
        description: reward.description || '',
        source: reward.source || 'signup',
        refereeId: reward.refereeId || '',
        refereeName: reward.refereeName || 'Unknown User',
        date: reward.date || new Date().toISOString()
      }));
    } catch (error) {
      console.error('Error fetching claimable rewards:', error);
      throw error;
    }
  }
  
  /**
   * Process and claim pending rewards
   */
  async claimRewards(): Promise<{ success: boolean; claimed: ClaimableReward[] }> {
    try {
      const response = await apiClient.post<any>('/user-referral/claim-rewards');
      
      return {
        success: response.success || false,
        claimed: response.claimed || []
      };
    } catch (error) {
      console.error('Error claiming rewards:', error);
      throw error;
    }
  }

  // ===== AMBASSADOR-RELATED METHODS =====

  /**
   * Check if current user is an ambassador
   */
  async getAmbassadorStatus(): Promise<AmbassadorStatus> {
    try {
      console.log('[AMBASSADOR] Fetching ambassador status');
      const response = await apiClient.get<any>('/ambassador/status');
      
      console.log('[AMBASSADOR] Ambassador status response:', response);
      
      return {
        isAmbassador: response.data?.isAmbassador || false,
        status: response.data?.status || null,
        application: response.data?.application || null
      };
    } catch (error) {
      console.error('[AMBASSADOR] Error fetching ambassador status:', error);
      // Return default values if user is not an ambassador or error occurs
      return {
        isAmbassador: false,
        status: null,
        application: null
      };
    }
  }

  /**
   * Get ambassador earnings
   */
  async getAmbassadorEarnings(): Promise<AmbassadorEarnings> {
    try {
      console.log('[AMBASSADOR] Fetching ambassador earnings');
      const response = await apiClient.get<any>('/ambassador/earnings');
      
      console.log('[AMBASSADOR] Ambassador earnings response:', response);
      
      return {
        totalEarnings: response.data?.totalEarnings || 0,
        pendingEarnings: response.data?.pendingEarnings || 0,
        totalPayouts: response.data?.totalPayouts || 0,
        totalReferrals: response.data?.totalReferrals || 0,
        successfulReferrals: response.data?.successfulReferrals || 0
      };
    } catch (error) {
      console.error('[AMBASSADOR] Error fetching ambassador earnings:', error);
      throw error;
    }
  }

  /**
   * Get ambassador payout history
   */
  async getAmbassadorPayouts(): Promise<PayoutHistoryResponse> {
    try {
      console.log('[AMBASSADOR] Fetching ambassador payouts');
      const response = await apiClient.get<any>('/ambassador/payouts');
      
      console.log('[AMBASSADOR] Ambassador payouts response:', response);
      
      return {
        payouts: response.data?.payouts || [],
        total: response.data?.total || 0
      };
    } catch (error) {
      console.error('[AMBASSADOR] Error fetching ambassador payouts:', error);
      throw error;
    }
  }

  /**
   * Format currency for display
   */
  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  /**
   * Format timestamp to readable date
   */
  formatTimestamp(timestamp: number): string {
    try {
      return new Date(timestamp * 1000).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return 'Invalid date';
    }
  }

  /**
   * Get payout status color for UI
   */
  getPayoutStatusColor(status: string): string {
    switch (status) {
      case 'processed':
        return 'text-green-600 bg-green-100';
      case 'processing':
        return 'text-blue-600 bg-blue-100';
      case 'queued':
        return 'text-yellow-600 bg-yellow-100';
      case 'pending':
        return 'text-orange-600 bg-orange-100';
      case 'failed':
      case 'cancelled':
      case 'reversed':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  }
}

// Export as singleton
const referralService = new ReferralService();
export default referralService; 